/* iter55o — Extracted from AdminHelpDrawer.jsx (was 403 LOC).
 *
 * Three pure/self-contained pieces that render admin-help content:
 *   • renderInline — sanitises text through DOMPurify + a small markdown
 *     substitution set (**bold**, *italic*, `code`). Defence-in-depth: the
 *     escape-then-substitute pipeline already produces safe HTML, but the
 *     DOMPurify allow-list is a hard backstop against future regressions.
 *   • Block — read-only renderer for one help block (paragraph / list /
 *     numbered step / tip / code).
 *   • BlockEditor — edit-mode counterpart. Same block kinds, inline form.
 */
import React from 'react';
import DOMPurify from 'dompurify';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Lightbulb } from 'lucide-react';

export function renderInline(text) {
  if (!text) return { __html: '' };
  let safe = String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.+?)\*/g, '<i>$1</i>')
    .replace(/`(.+?)`/g, '<code class="rounded-sm bg-muted px-1 text-[11px]">$1</code>');
  safe = DOMPurify.sanitize(safe, {
    ALLOWED_TAGS: ['b', 'i', 'code'],
    ALLOWED_ATTR: ['class'],
  });
  return { __html: safe };
}

export function Block({ b }) {
  if (b.kind === 'para')
    return <p className="text-[12.5px] leading-relaxed" dangerouslySetInnerHTML={renderInline(b.text)} />;
  if (b.kind === 'list')
    return (
      <ul className="ml-4 list-disc space-y-1 text-[12.5px]">
        {(b.items || []).map((it, i) => (
          // iter-53h — Content-derived key so reordering / deletion
          // doesn't desync rendered state with the underlying list.
          <li key={`${i}-${String(it).slice(0, 24)}`} dangerouslySetInnerHTML={renderInline(it)} />
        ))}
      </ul>
    );
  if (b.kind === 'step')
    return (
      <div className="flex gap-2 text-[12.5px]">
        <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">{b.n ?? '·'}</span>
        <span dangerouslySetInnerHTML={renderInline(b.text)} />
      </div>
    );
  if (b.kind === 'tip')
    return (
      <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
        <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span dangerouslySetInnerHTML={renderInline(b.text)} />
      </div>
    );
  if (b.kind === 'code')
    return <pre className="overflow-x-auto rounded-md bg-muted p-2 font-mono-stat text-[11px]">{b.text}</pre>;
  return null;
}

const KINDS = [
  { v: 'para', l: 'Paragraph' },
  { v: 'list', l: 'Bullet list' },
  { v: 'step', l: 'Numbered step' },
  { v: 'tip',  l: 'Tip' },
  { v: 'code', l: 'Code' },
];

export function BlockEditor({ block, onChange, onRemove }) {
  return (
    <div className="rounded-md border border-border bg-card/50 p-2 space-y-1.5">
      <div className="flex items-center justify-between text-[10.5px]">
        <select
          value={block.kind}
          onChange={(e) => onChange({ ...block, kind: e.target.value })}
          className="h-6 rounded-sm border border-border bg-background px-1 text-[11px]"
        >
          {KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <button onClick={onRemove} className="text-destructive hover:underline">remove</button>
      </div>
      {block.kind === 'list' ? (
        <Textarea
          rows={3}
          value={(block.items || []).join('\n')}
          onChange={(e) => onChange({ ...block, items: e.target.value.split('\n').map(s => s.trimEnd()).filter(Boolean) })}
          placeholder="One bullet per line"
          className="text-[11.5px]"
        />
      ) : block.kind === 'step' ? (
        <div className="flex gap-1.5">
          <Input
            type="number"
            value={block.n ?? 1}
            onChange={(e) => onChange({ ...block, n: parseInt(e.target.value || '1', 10) })}
            className="h-7 w-14 text-[11px]"
          />
          <Textarea
            rows={2}
            value={block.text || ''}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            className="text-[11.5px]"
          />
        </div>
      ) : (
        <Textarea
          rows={block.kind === 'tip' ? 2 : 3}
          value={block.text || ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          className="text-[11.5px]"
        />
      )}
    </div>
  );
}
